import { supabase } from "../supabaseClient";

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
  maxRetries?: number;
}

interface ApiClientConfig {
  onAuthError?: () => void;
  onTokenRefresh?: (session: any) => void;
}

class ApiClient {
  private config: ApiClientConfig = {};
  private refreshPromise: Promise<any> | null = null;

  configure(config: ApiClientConfig) {
    this.config = config;
  }

  private async refreshToken() {
    // Si ya hay un refresh en curso, esperar a que termine
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        
        if (error || !data.session) {
          console.error("Failed to refresh session:", error);
          this.config.onAuthError?.();
          throw new Error("Session refresh failed");
        }

        this.config.onTokenRefresh?.(data.session);
        return data.session;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private isAuthError(error: any, response?: Response): boolean {
    if (!response) return false;
    
    // Errores de autenticación HTTP
    if (response.status === 401 || response.status === 403) {
      return true;
    }

    // Errores de Supabase/JWT
    if (error?.message) {
      const msg = error.message.toLowerCase();
      return (
        msg.includes("jwserror") ||
        msg.includes("invalid json") ||
        msg.includes("jwt") ||
        msg.includes("token")
      );
    }

    if (error?.code === "PGRST301") {
      return true;
    }

    return false;
  }

  async fetch(
    url: string,
    options: FetchOptions = {},
    session?: any
  ): Promise<Response> {
    const { skipAuth = false, maxRetries = 2, ...fetchOptions } = options;

    // Si no se debe incluir autenticación, hacer fetch directo
    if (skipAuth) {
      return fetch(url, fetchOptions);
    }

    // Asegurar que tenemos una sesión válida
    if (!session) {
      const { data } = await supabase.auth.getSession();
      session = data.session;
    }

    if (!session) {
      console.error("No session available for authenticated request");
      this.config.onAuthError?.();
      throw new Error("No session available");
    }

    // Configurar headers de autenticación
    const existingHeaders = fetchOptions.headers as Record<string, string> | undefined;
    const headers = {
      ...fetchOptions.headers,
      Authorization: `${session.token_type} ${session.access_token}`,
      "Content-Type": existingHeaders?.["Content-Type"] || "application/json",
    };

    let lastError: any = null;
    let lastResponse: Response | undefined = undefined;

    // Intentar la petición con reintentos
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          ...fetchOptions,
          headers,
        });

        lastResponse = response;

        // Si la respuesta es exitosa, retornarla
        if (response.ok) {
          return response;
        }

        // Si es un error de autenticación y no es el último intento
        if (this.isAuthError(null, response) && attempt < maxRetries) {
          console.log(`Auth error detected on attempt ${attempt + 1}, refreshing token...`);
          
          // Refrescar el token
          const newSession = await this.refreshToken();
          
          // Actualizar headers con el nuevo token
          headers.Authorization = `${newSession.token_type} ${newSession.access_token}`;
          session = newSession;
          
          continue; // Reintentar con el nuevo token
        }

        // Si no es un error de auth o ya agotamos los reintentos, retornar la respuesta
        return response;

      } catch (error: any) {
        lastError = error;
        
        // Si es un error de autenticación y no es el último intento
        if (this.isAuthError(error, lastResponse) && attempt < maxRetries) {
          console.log(`Auth error detected on attempt ${attempt + 1}, refreshing token...`);
          
          try {
            const newSession = await this.refreshToken();
            headers.Authorization = `${newSession.token_type} ${newSession.access_token}`;
            session = newSession;
            continue;
          } catch (refreshError) {
            console.error("Failed to refresh token:", refreshError);
            throw refreshError;
          }
        }

        // Si no es un error de auth o ya agotamos los reintentos, lanzar el error
        if (attempt === maxRetries) {
          throw error;
        }
      }
    }

    // Si llegamos aquí, algo salió mal
    if (lastError) throw lastError;
    if (lastResponse && !lastResponse.ok) return lastResponse;
    
    throw new Error("Request failed after all retries");
  }

  async get(url: string, options: FetchOptions = {}, session?: any): Promise<Response> {
    return this.fetch(url, { ...options, method: "GET" }, session);
  }

  async post(url: string, body?: any, options: FetchOptions = {}, session?: any): Promise<Response> {
    return this.fetch(
      url,
      {
        ...options,
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      },
      session
    );
  }

  async patch(url: string, body?: any, options: FetchOptions = {}, session?: any): Promise<Response> {
    return this.fetch(
      url,
      {
        ...options,
        method: "PATCH",
        body: body ? JSON.stringify(body) : undefined,
      },
      session
    );
  }

  async delete(url: string, body?: any, options: FetchOptions = {}, session?: any): Promise<Response> {
    return this.fetch(
      url,
      {
        ...options,
        method: "DELETE",
        body: body ? JSON.stringify(body) : undefined,
      },
      session
    );
  }

  async put(url: string, body?: any, options: FetchOptions = {}, session?: any): Promise<Response> {
    return this.fetch(
      url,
      {
        ...options,
        method: "PUT",
        body: body ? JSON.stringify(body) : undefined,
      },
      session
    );
  }
}

// Exportar una instancia singleton
export const apiClient = new ApiClient();

// Hook personalizado para usar en componentes React
export const useApiClient = () => {
  return apiClient;
};
